import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Form } from '@unform/web';
import { useDispatch, useSelector } from 'react-redux';
import { subDays, addDays, format, getTime, getDate, parseISO } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import api from 'services/api';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import { show } from 'store/modules/dialog/actions';
import midiainfo from 'utils/midiainfo';

import Input from 'components/Input';
import Card from 'components/Card';
import SelectDate from 'components/SelectDate';
import Tooltip from 'components/Tooltip';
import Flag from 'components/Flag';
import DropDownMenu from 'components/DropDownMenu';
import Loader from 'components/ContentLoader/ComerciaisNaoGravadosLoader';
import Switch from 'components/Switch/withoutForm';
import PlayerBaixa from 'components/PlayerBaixa';
import Button from 'components/Button';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';

import {
  FiActivity,
  FiArchive,
  FiArrowLeft,
  FiInfo,
  FiPlay,
  FiAlertTriangle,
  FiRefreshCw,
  FiMessageSquare,
  FiCheck,
  FiSearch,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { FaRegCalendarTimes } from 'react-icons/fa';
import { IoMdHourglass } from 'react-icons/io';

import NoPedingData from 'assets/nopendingdata.svg';

import { Scroll } from 'styles/global';
import { Td, TdFlag } from 'pages/Comercial/styles';
import { Container, Table } from './styles';

import {
  Comercial as IComercial,
  Midia as IMidia,
  EnviaExibidor as IEnviaExibidor,
  Emissora,
} from 'pages/@types';
import AppError from 'utils/AppError';
import { Role, StatusPreAvaliacao } from 'utils/variables';

interface Midia extends IMidia {
  usuario?: { nome: string; avatar_url: string };
  statuspreavaliacao16ch: string;
}

interface Comercial extends IComercial {
  midia: Midia;
}
interface EnviaExibidor extends IEnviaExibidor {
  loading: boolean;
  emissoraorigem: Emissora;
  roteiro?: {
    abrangencia: string;
    codmaterial: number;
    ippt: string;
    numippt: number;
    roteiro: { datautilizacao: string };
    programa: { nome: string };
  };
  comercial: Comercial;
}

export default function ComerciaisNaoGravados() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * REDUX
   * */
  const dispatch = useDispatch();
  const { emissora, currentDate, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /*
   * VARIAVEIS
   */

  const [selected, setSelected] = useState<EnviaExibidor | null>(null);
  const [loading, setLoading] = useState(true);
  const [roteiroCarregado, setRoteiroCarregado] = useState(false);
  const [naoGravadosCompleto, setNaoGravadosCompleto] = useState<
    EnviaExibidor[]
  >([]);
  const [filtro, setFiltro] = useState('');
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });
  // const [loadingDownload, setLoadingDownload] = useState(false);

  const naoGravados = naoGravadosCompleto.filter(
    gra =>
      gra.comercial.titulo.includes(filtro.toUpperCase()) ||
      gra.comercial.codmaterial.toString().includes(filtro) ||
      gra.comercial.cliente.mnemonico.includes(filtro.toUpperCase()),
  );

  /**
   * USE MEMO
   */

  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    } else return format(new Date(), "d 'de' MMMM", { locale: pt });
  }, [currentDate]);

  /**
   * FUNÇÕES
   */

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleSwitchChange = useCallback(
    async (e, gra) => {
      const globoexpress = e.target.checked;

      setNaoGravadosCompleto(
        naoGravados.map(c =>
          c.id === gra.id ? { ...c, loading: true } : { ...c },
        ),
      );

      try {
        await api.put(`/globoexpress/${gra.comercial.id}`, {
          globoexpress,
        });
        setNaoGravadosCompleto(
          naoGravados.map(c =>
            c.id === gra.id
              ? {
                  ...c,
                  comercial: { ...c.comercial, globoexpress: globoexpress },
                  loading: false,
                }
              : { ...c },
          ),
        );

        toast.success('Prioridade alterada');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    },
    [naoGravados],
  );

  const handlePlayer = useCallback(
    midia => {
      if (!!midia && midia.preview_url) {
        setPlayer({
          open: !player.open,
          midia_url: midia.preview_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const handleRessalva = useCallback(
    gra => {
      const contentHTML = `<p>Observação: ${gra.statusgeral.info}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );
  const handleMidiaInfoDialog = useCallback(
    async id => {
      const response = await api.get(`/midias/${id}`);
      if (response && response.data && response.data.pendente) {
        const comercial = response.data.pendente;
        if (comercial && comercial.midia && comercial.midia.midiainfo) {
          if (
            comercial.midia.midiainfo8ch &&
            comercial.midia.statuspreavaliacao8ch
          ) {
            const options = midiainfo(
              comercial.midia.statuspreavaliacao8ch,
              comercial.midia.midiainfo8ch,
            );
            dispatch(show(options));
          }
        }
      } else toast.error('Falha de comunicação com o sistema');
    },
    [dispatch],
  );

  /**
   * USE CALLBACK
   */

  // const handleHistorico = useCallback(id => {
  //   history.push(`/historico/${id}`);
  // }, []);
  // const handleBack = useCallback(() => {
  //   history.goBack();
  // }, []);

  function handleActionSelect(c: EnviaExibidor, action: { value: number }) {
    const optionBase = {
      type: 'question',

      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/enviaexibidor/${c.id}`,
        method: api.patch,
      },
      haveCallback: true,
      successMessage: 'Status alterado!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };

    let options = {};

    setSelected(c);
    switch (action.value) {
      case 1: // INGEST MANUAL
        options = {
          ...optionBase,
          contentHTML: `<p>Confirma a gravação de <b>${c.comercial.titulo}</b>?</p>`,
          callback: {
            ...optionBase.callback,
            data: { status: 'ingestmanual' },
            action: 'ingestmanual',
          },
        };
        dispatch(show(options));
        break;
      case 2:
        options = {
          ...optionBase,
          contentHTML: `<p>Iniciar um novo envio do material <b>${c.comercial.titulo}</b> para o exibidor?</p>`,
          callback: {
            action: 'reenvio',
            ...optionBase.callback,
            data: { status: 'pendente' },
          },
        };
        dispatch(show(options));
        break;

      case 3:
        handlePlayer(c.comercial.midia);
        break;
      case 4:
        options = {
          ...optionBase,
          contentHTML: `Arquivar ${c.comercial.titulo}?`,
          callback: {
            url: `/enviaexibidor/${c.id}`,
            method: api.delete,
            action: 'arquivar',
          },
        };
        dispatch(show(options));
        break;
      case 5:
        if (c.comercial.midia && c.comercial.midia.id)
          handleMidiaInfoDialog(c.comercial.midia.id);
        break;
      case 6:
        options = {
          ...optionBase,
          contentHTML: `Deseja autorizar o envio de ${c.comercial.titulo}?`,
          callback: {
            url: `/enviaexibidor/${c.id}`,
            data: { autorizado: true },
            method: api.patch,
            action: 'autoriza',
          },
          successMessage: 'Envio autorizado!',
        };
        dispatch(show(options));
        break;
      default:
        break;
    }
  }

  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadNaoGravados() {
      setLoading(true);
      try {
        const response = await api.get(
          `/emissoras/${emissora.value}/comerciaisnaogravados`,
          {
            signal: controller.signal,
            params: { date: getTime(new Date(currentDate)) },
          },
        );

        if (response && response.data) {
          setRoteiroCarregado(response.data.roteiroCarregado);
          setNaoGravadosCompleto(response.data.comerciaisNaoGravados);
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
      setLoading(false);
    }
    loadNaoGravados();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'ingestmanual' && selected) {
        setNaoGravadosCompleto(prevState =>
          prevState.filter(gra => gra.id !== selected.id),
        );
      } else if (
        (callbackDialog.success.action === 'reenvio' ||
          callbackDialog.success.action === 'autoriza') &&
        selected
      ) {
        setNaoGravadosCompleto(prevState =>
          prevState.map(gra =>
            gra.id === selected.id
              ? {
                  ...gra,
                  statusgeral: {
                    order: 16,
                    nome: 'FILA ENVIO EXIBIDOR',
                    type: 'fila_envio',
                    info: 'Midia na fila para envio ao exibidor',
                  },
                }
              : gra,
          ),
        );
      } else if (callbackDialog.success.action === 'arquivar' && selected) {
        setNaoGravadosCompleto(prevState =>
          prevState.map(gra =>
            gra.id === selected.id
              ? {
                  ...gra,
                  deleted_at: new Date().toString(),
                  statusgeral: {
                    order: 14,
                    nome: 'ARQUIVADO',
                    type: 'arquivado',
                    info: 'Envio arquivado',
                  },
                }
              : gra,
          ),
        );
      }
      setSelected(null);
    }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <nav>
        <Button color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>

        <SelectDate
          dateFormatted={dateFormatted}
          currentDateZoned={format(
            addDays(new Date(currentDate), 1),
            'yyyy-MM-dd',
          )}
          handleNextDay={handleNextDay}
          handleDateChange={handleDateChange}
          handlePrevDay={handlePrevDay}
          disabledNextDay={
            getDate(new Date(currentDate)) === getDate(new Date())
          }
        />
        <div />
      </nav>
      <Card>
        {loading ? (
          <Loader />
        ) : (
          <>
            <header>
              <h4>COMERCIAIS NÃO GRAVADOS</h4>

              <div>
                <Form onSubmit={() => {}}>
                  <Input
                    name="searchLocal"
                    placeholder="Buscar na Tabela"
                    icon={<FiSearch />}
                    onChange={e => setFiltro(e.target.value)}
                  />
                </Form>
              </div>
            </header>
            {naoGravados.length === 0 ? (
              <div>
                <NotFound img={NoPedingData} title="COMERCIAIS NÃO GRAVADOS">
                  Nenhum comercial pendente de gravação encontrado
                </NotFound>
              </div>
            ) : (
              <Scroll>
                <Table>
                  <thead>
                    <tr>
                      <th />
                      <th style={{ width: '4rem' }}>CM</th>
                      <th>TITULO</th>
                      <th style={{ width: '10rem', textAlign: 'center' }}>
                        CATEGORIA
                      </th>
                      <th style={{ width: '1rem' }}>ROTEIRO</th>
                      <th style={{ width: '10rem', textAlign: 'center' }}>
                        STATUS
                      </th>
                      <th>OBS.</th>
                      <th style={{ width: '4rem', textAlign: 'center' }}>
                        AVAL.
                      </th>
                      <th style={{ width: '8rem', textAlign: 'center' }}>
                        PRIORIDADE
                      </th>
                      <th style={{ width: '4rem', textAlign: 'center' }}>
                        ORIGEM
                      </th>

                      <th style={{ width: '1rem' }} />
                    </tr>
                  </thead>

                  <tbody>
                    {naoGravados.map((gra, index) => (
                      <tr key={gra.id}>
                        <TdFlag $isGe={gra.comercial.globoexpress}>
                          <div id={`tdFlag_id${index}`} />
                          <Tooltip
                            target={`tdFlag_id${index}`}
                            position="right"
                          >
                            <p>
                              <strong>PRIORITÁRIO:</strong> GLOBO EXPRESS
                            </p>
                          </Tooltip>
                        </TdFlag>
                        <Td disabled={gra.deleted_at !== null}>
                          {gra.comercial.codmaterial}
                        </Td>
                        <Td disabled={gra.deleted_at !== null}>
                          {gra.comercial.titulo}{' '}
                          <strong>({gra.comercial.cliente.mnemonico})</strong>
                        </Td>
                        <Td
                        // style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Flag
                            disabled={gra.deleted_at !== null}
                            type={gra.comercial.categoria}
                            name={gra.comercial.categoria}
                          />
                        </Td>
                        <Td
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Button
                            fill
                            round
                            color={
                              gra.roteiro && gra.roteiro.abrangencia !== 'R'
                                ? 'warn'
                                : 'font'
                            }
                            id={`id_icon_${index}`}
                          >
                            {roteiroCarregado ? (
                              gra.roteiro ? (
                                gra.roteiro.abrangencia === 'R' ? (
                                  <FiAlertCircle />
                                ) : (
                                  <FiAlertTriangle />
                                )
                              ) : (
                                <FaRegCalendarTimes />
                              )
                            ) : (
                              <IoMdHourglass />
                            )}
                          </Button>
                          <Tooltip target={`id_icon_${index}`} position="left">
                            {roteiroCarregado
                              ? gra.roteiro
                                ? `Exibição ${
                                    gra.roteiro.abrangencia === 'R'
                                      ? 'REDE'
                                      : 'LOCAL'
                                  } programada em ${format(
                                    parseISO(
                                      gra.roteiro.roteiro.datautilizacao,
                                    ),
                                    'dd/MM/yyyy',
                                  )} na ${gra.roteiro.ippt}${
                                    gra.roteiro.numippt
                                  } do ${gra.roteiro.programa.nome}`
                                : 'Exibição não programada em roteiro'
                              : 'Roteiro não carregado'}
                          </Tooltip>
                        </Td>
                        <Td
                        // style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Flag
                            disabled={gra.deleted_at !== null}
                            type={gra.statusgeral.type}
                            name={gra.statusgeral.nome}
                          />
                        </Td>
                        <Td>
                          {gra.statusgeral.info && (
                            <Button
                              fill
                              color="font"
                              round
                              onClick={() => handleRessalva(gra)}
                            >
                              <FiMessageSquare />
                            </Button>
                          )}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <Avatar
                            size="40"
                            name={gra.comercial.midia?.usuario?.nome}
                            src={gra.comercial.midia?.usuario?.avatar_url}
                            round
                          />
                        </Td>

                        <Td>
                          <Switch
                            disabled={gra.deleted_at !== null}
                            id={gra.comercial.id}
                            loading={gra.comercial.loading || false}
                            // loading
                            onChange={e => handleSwitchChange(e, gra)}
                            checked={gra.comercial.globoexpress}

                            // disabled={
                            //   c.comercial.midia.avaliacaoeng !== 'pendente'
                            // }
                          />
                        </Td>
                        <Td>
                          {gra.emissoraorigem && gra.emissoraorigem.sigla}
                        </Td>
                        <Td>
                          <DropDownMenu
                            disabled={false}
                            id={`acoes${index}`}
                            actions={[
                              {
                                value: 0,
                                label: 'HISTÓRICO',
                                icon: <FiActivity />,
                                href: `/historico/${gra.comercial.id}`,
                              },
                              {
                                value: 1,
                                label: 'GRAVADO',
                                icon: <FiCheckCircle />,
                                disabled: gra.deleted_at !== null,
                              },
                              {
                                value: 2,
                                label: 'REENVIAR',
                                icon: <FiRefreshCw />,
                                disabled:
                                  gra.deleted_at !== null ||
                                  (gra.statusgeral.type !== 'fila_envio' &&
                                    gra.statusgeral.type !== 'erro_envio' &&
                                    gra.statusgeral.type !==
                                      'enviando_exibidor'),
                              },
                              {
                                value: 3,
                                label: 'ASSISTIR',
                                icon: <FiPlay />,
                                disabled:
                                  gra.deleted_at !== null ||
                                  !gra.comercial ||
                                  !gra.comercial.midia ||
                                  !gra.comercial.midia.preview_url,
                              },
                              {
                                value: 4,
                                label: 'ARQUIVAR',
                                icon: <FiArchive />,
                                disabled: gra.deleted_at !== null,
                              },
                              {
                                value: 5,
                                label: 'MIDIAINFO',
                                icon: <FiInfo />,
                                disabled:
                                  gra.deleted_at !== null ||
                                  !gra.comercial ||
                                  !gra.comercial.midia ||
                                  (gra.comercial.midia
                                    .statuspreavaliacao16ch !==
                                    StatusPreAvaliacao.APROVADO &&
                                    gra.comercial.midia
                                      .statuspreavaliacao16ch !==
                                      StatusPreAvaliacao.REPROVADO),
                              },
                              {
                                value: 6,
                                label: 'AUTORIZAR',
                                icon: <FiCheck />,
                                disabled:
                                  gra.deleted_at !== null ||
                                  gra.allowed_api_at !== null ||
                                  (profile.role !== Role.ADMIN &&
                                    profile.role !== Role.OPEC),
                              },
                            ]}
                            handleClick={action =>
                              !action.disabled &&
                              handleActionSelect(gra, action)
                            }
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Scroll>
            )}
          </>
        )}
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
